/**
 * DocumentManager — multi-document support for excalidraw-app.
 *
 * Owns the list of documents (see `documentStorage.ts`), tracks the active
 * document, autosaves scene changes into it (debounced), and handles
 * switching/creating/renaming/duplicating/deleting documents.
 *
 * Documents are named with path-like hierarchical names, segments separated
 * by `/` (e.g. `work/client-a/logo`), which the sidebar renders as a folder
 * tree.
 */

import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { debounce, randomId } from "@excalidraw/common";
import {
  getNonDeletedElements,
  isInitializedImageElement,
} from "@excalidraw/element";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { appJotaiStore, atom } from "../app-jotai";
import { SAVE_TO_LOCAL_STORAGE_TIMEOUT, STORAGE_KEYS } from "../app_constants";
import { commentStorage } from "../comments/commentStorage";
import { LocalData } from "../data/LocalData";
import { updateStaleImageStatuses } from "../data/FileManager";

import { documentStorage } from "./documentStorage";
import {
  CloudDocumentError,
  createCloudDocument,
  deleteCloudDocument,
  hasCloudUser,
  hydrateCloudDocuments,
  importGuestDocuments,
  saveCloudDocument,
  setCloudUser,
} from "./cloudDocuments";

import type { DocumentMeta, StoredDocumentData } from "./documentStorage";

export const documentsAtom = atom<DocumentMeta[]>([]);
export const activeDocumentIdAtom = atom<string | null>(null);

const DEFAULT_DOCUMENT_NAME = "Untitled";

const serializeDocumentData = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
): StoredDocumentData => ({
  elements: getNonDeletedElements(elements) as ExcalidrawElement[],
  appState: clearAppStateForLocalStorage(appState),
});

// ---------------------------------------------------------------------------
// name helpers
// ---------------------------------------------------------------------------

/** Trims segments and strips empty ones: `a//b/ ` -> `a/b`. Returns "" if
 * nothing usable remains. */
export const normalizeDocumentName = (name: string): string =>
  name
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join("/");

export const getDocumentBasename = (name: string): string => {
  const segments = name.split("/");
  return segments[segments.length - 1];
};

/** Appends ` (2)`, ` (3)`, ... if the name is already taken. */
const uniqueDocumentName = (
  desired: string,
  index: DocumentMeta[],
  excludeId?: string,
): string => {
  const taken = new Set(
    index.filter((meta) => meta.id !== excludeId).map((meta) => meta.name),
  );
  if (!taken.has(desired)) {
    return desired;
  }
  let i = 2;
  while (taken.has(`${desired} (${i})`)) {
    i++;
  }
  return `${desired} (${i})`;
};

// ---------------------------------------------------------------------------
// active document id (persisted so reloads reopen the same document)
// ---------------------------------------------------------------------------

const readPersistedActiveDocumentId = (): string | null => {
  try {
    const scope = documentStorage.getScope();
    const key =
      scope === "guest"
        ? STORAGE_KEYS.LOCAL_STORAGE_ACTIVE_DOCUMENT
        : `${STORAGE_KEYS.LOCAL_STORAGE_ACTIVE_DOCUMENT}:${scope}`;
    return localStorage.getItem(key);
  } catch (error: any) {
    console.error(error);
    return null;
  }
};

const setActiveDocumentId = (id: string) => {
  appJotaiStore.set(activeDocumentIdAtom, id);
  try {
    const scope = documentStorage.getScope();
    const key =
      scope === "guest"
        ? STORAGE_KEYS.LOCAL_STORAGE_ACTIVE_DOCUMENT
        : `${STORAGE_KEYS.LOCAL_STORAGE_ACTIVE_DOCUMENT}:${scope}`;
    localStorage.setItem(key, id);
  } catch (error: any) {
    console.error(error);
  }
};

// ---------------------------------------------------------------------------
// autosave
// ---------------------------------------------------------------------------

const persistDocumentData = async (
  documentId: string,
  data: StoredDocumentData,
  files: BinaryFiles,
  scope: string,
  generation: number,
) => {
  try {
    await documentStorage.saveDocument(documentId, data, scope);

    if (
      !isCurrentScope(scope, generation) ||
      invalidatedCloudDocuments.has(cloudQueueKey(scope, documentId))
    ) {
      return;
    }

    const nextIndex = appJotaiStore.get(documentsAtom).map((meta) =>
      meta.id === documentId
        ? {
            ...meta,
            updatedAt: Date.now(),
            syncState: hasCloudUser(scope)
              ? ("saving" as const)
              : ("local" as const),
          }
        : meta,
    );
    appJotaiStore.set(documentsAtom, nextIndex);
    await documentStorage.saveIndex(nextIndex, scope);
    queueCloudSave(documentId, data, files, scope, generation);
  } catch (error: any) {
    console.error(error);
  }
};

type CloudSaveSnapshot = {
  data: StoredDocumentData;
  files: BinaryFiles;
};

const cloudSaveQueues = new Map<string, Promise<void>>();
const latestCloudSnapshots = new Map<string, CloudSaveSnapshot>();
const invalidatedCloudDocuments = new Set<string>();
const localSavePromises = new Set<Promise<void>>();
let scopeGeneration = 0;
let switchQueue: Promise<unknown> = Promise.resolve();
let documentSavesPaused = false;

const cloudQueueKey = (scope: string, documentId: string) =>
  `${scope}:${documentId}`;

const isCurrentScope = (scope: string, generation: number) =>
  documentStorage.getScope() === scope && scopeGeneration === generation;

const stageCloudSnapshot = (
  documentId: string,
  data: StoredDocumentData,
  files: BinaryFiles,
  scope: string,
) => {
  if (hasCloudUser(scope)) {
    latestCloudSnapshots.set(cloudQueueKey(scope, documentId), { data, files });
  }
};

const trackLocalSave = (promise: Promise<void>) => {
  localSavePromises.add(promise);
  promise.finally(() => localSavePromises.delete(promise));
};

const setDocumentSyncState = async (
  documentId: string,
  update: Partial<DocumentMeta>,
  scope: string,
  generation: number,
) => {
  if (!isCurrentScope(scope, generation)) {
    return;
  }
  const nextIndex = appJotaiStore
    .get(documentsAtom)
    .map((meta) => (meta.id === documentId ? { ...meta, ...update } : meta));
  appJotaiStore.set(documentsAtom, nextIndex);
  await documentStorage.saveIndex(nextIndex, scope);
};

const preserveCloudConflict = async (
  documentId: string,
  data: StoredDocumentData,
  files: BinaryFiles,
  remoteDocument: any,
  scope: string,
  generation: number,
) => {
  if (!isCurrentScope(scope, generation)) {
    return false;
  }
  if (!remoteDocument?.data || !remoteDocument?.revision) {
    return false;
  }
  const index = appJotaiStore.get(documentsAtom);
  const meta = index.find((item) => item.id === documentId);
  if (!meta) {
    return false;
  }

  const now = Date.now();
  const conflictMeta: DocumentMeta = {
    id: randomId(),
    name: uniqueDocumentName(`${meta.name} (conflict copy)`, index),
    createdAt: now,
    updatedAt: now,
    syncState: "saving",
  };
  const restoredRemote: DocumentMeta = {
    id: remoteDocument.id,
    name: remoteDocument.name,
    revision: remoteDocument.revision,
    createdAt: remoteDocument.createdAt,
    updatedAt: remoteDocument.updatedAt,
    syncState: "saved",
  };

  const nextIndex = [
    ...index.map((item) => (item.id === documentId ? restoredRemote : item)),
    conflictMeta,
  ];
  const originalSave = documentStorage.saveDocument(
    documentId,
    remoteDocument.data,
    scope,
  );
  const conflictSave = documentStorage.saveDocument(
    conflictMeta.id,
    data,
    scope,
  );
  appJotaiStore.set(documentsAtom, nextIndex);
  const indexSave = documentStorage.saveIndex(nextIndex, scope);
  invalidatedCloudDocuments.add(cloudQueueKey(scope, documentId));

  const conflictKey = cloudQueueKey(scope, conflictMeta.id);
  stageCloudSnapshot(conflictMeta.id, data, files, scope);
  const createPromise = Promise.all([conflictSave, indexSave])
    .then(() => createCloudDocument(conflictMeta, data, files))
    .then((cloudCopy) =>
      setDocumentSyncState(conflictMeta.id, cloudCopy, scope, generation),
    )
    .catch(async (error) => {
      await setDocumentSyncState(
        conflictMeta.id,
        { syncState: "offline" },
        scope,
        generation,
      );
      console.error(error);
    })
    .finally(() => {
      if (cloudSaveQueues.get(conflictKey) === createPromise) {
        cloudSaveQueues.delete(conflictKey);
        latestCloudSnapshots.delete(conflictKey);
      }
    });
  cloudSaveQueues.set(conflictKey, createPromise);
  if (appJotaiStore.get(activeDocumentIdAtom) === documentId) {
    setActiveDocumentId(conflictMeta.id);
  }
  await Promise.all([originalSave, createPromise]);
  return true;
};

const queueCloudSave = (
  documentId: string,
  data: StoredDocumentData,
  files: BinaryFiles,
  scope: string,
  generation: number,
) => {
  if (!hasCloudUser(scope) || !isCurrentScope(scope, generation)) {
    return;
  }
  const key = cloudQueueKey(scope, documentId);
  const snapshot = { data, files };
  if (!latestCloudSnapshots.has(key)) {
    latestCloudSnapshots.set(key, snapshot);
  }
  const previous = cloudSaveQueues.get(key) || Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(async () => {
      if (
        invalidatedCloudDocuments.has(key) ||
        !isCurrentScope(scope, generation)
      ) {
        return;
      }
      const meta = appJotaiStore
        .get(documentsAtom)
        .find((meta) => meta.id === documentId);
      if (!meta) {
        return;
      }
      try {
        const saved = await saveCloudDocument(meta, data, files);
        await setDocumentSyncState(documentId, saved, scope, generation);
      } catch (error) {
        if (error instanceof CloudDocumentError && error.status === 409) {
          try {
            const latestSnapshot = latestCloudSnapshots.get(key) || snapshot;
            if (
              await preserveCloudConflict(
                documentId,
                latestSnapshot.data,
                latestSnapshot.files,
                error.body?.document,
                scope,
                generation,
              )
            ) {
              return;
            }
          } catch (conflictError) {
            console.error(conflictError);
          }
        }
        await setDocumentSyncState(
          documentId,
          { syncState: "offline" },
          scope,
          generation,
        );
        console.error(error);
      }
    })
    .finally(() => {
      if (cloudSaveQueues.get(key) === next) {
        cloudSaveQueues.delete(key);
        const latest = latestCloudSnapshots.get(key);
        if (latest?.data === data && latest.files === files) {
          latestCloudSnapshots.delete(key);
        }
      }
    });
  cloudSaveQueues.set(key, next);
};

// document id a pending debounced save will write to (null when idle)
let pendingSaveDocumentId: string | null = null;
let pendingSaveScope: string | null = null;

const _saveActiveDocument = debounce(
  (
    documentId: string,
    data: StoredDocumentData,
    files: BinaryFiles,
    scope: string,
    generation: number,
  ) => {
    pendingSaveDocumentId = null;
    pendingSaveScope = null;
    if (invalidatedCloudDocuments.has(cloudQueueKey(scope, documentId))) {
      return;
    }
    stageCloudSnapshot(documentId, data, files, scope);
    const save = persistDocumentData(
      documentId,
      data,
      files,
      scope,
      generation,
    );
    trackLocalSave(save);
  },
  SAVE_TO_LOCAL_STORAGE_TIMEOUT,
);

/**
 * Debounce-persists the scene into the currently active document. The active
 * document id is captured at call time so a pending save always targets the
 * document the data belongs to, even if the user switches documents before
 * the timeout fires.
 */
export const scheduleDocumentSave = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
) => {
  if (documentSavesPaused) {
    return;
  }
  const documentId = appJotaiStore.get(activeDocumentIdAtom);
  if (!documentId) {
    return;
  }
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  const data = serializeDocumentData(elements, appState);
  stageCloudSnapshot(documentId, data, files, scope);
  pendingSaveDocumentId = documentId;
  pendingSaveScope = scope;
  _saveActiveDocument(documentId, data, files, scope, generation);
};

/** Drops a pending autosave targeting the given document (e.g. before it's
 * deleted, so the write can't resurrect it). */
export const cancelDocumentSave = (documentId: string) => {
  if (pendingSaveDocumentId === documentId) {
    pendingSaveDocumentId = null;
    pendingSaveScope = null;
    _saveActiveDocument.cancel();
  }
};

/** Immediately writes any pending autosave (e.g. on unload or before
 * switching documents). */
export const flushDocumentSave = () => {
  _saveActiveDocument.flush();
};

export const flushDocumentSaves = async () => {
  do {
    _saveActiveDocument.flush();
    await Promise.allSettled([...localSavePromises]);
    await Promise.allSettled([...cloudSaveQueues.values()]);
  } while (
    pendingSaveDocumentId ||
    localSavePromises.size > 0 ||
    cloudSaveQueues.size > 0
  );
};

export const shouldPreventDocumentUnload = () =>
  !!pendingSaveDocumentId ||
  !!pendingSaveScope ||
  localSavePromises.size > 0 ||
  cloudSaveQueues.size > 0;

// ---------------------------------------------------------------------------
// initialization & migration
// ---------------------------------------------------------------------------

let initialized = false;

const prepareDocumentScopeNow = async (userId: string | null) => {
  documentSavesPaused = true;
  try {
    await switchQueue.catch(() => undefined);
    await flushDocumentSaves();
    scopeGeneration++;
    pendingSaveDocumentId = null;
    pendingSaveScope = null;
    cloudSaveQueues.clear();
    latestCloudSnapshots.clear();
    invalidatedCloudDocuments.clear();
    initialized = false;
    appJotaiStore.set(documentsAtom, []);
    appJotaiStore.set(activeDocumentIdAtom, null);
    documentStorage.setScope(userId);
    setCloudUser(userId);
    if (userId) {
      await hydrateCloudDocuments(userId);
    }
  } finally {
    documentSavesPaused = false;
  }
};

let scopeTransition: Promise<void> = Promise.resolve();

export const prepareDocumentScope = (userId: string | null) => {
  const transition = scopeTransition.then(() =>
    prepareDocumentScopeNow(userId),
  );
  scopeTransition = transition.catch(() => undefined);
  return transition;
};

export const importGuestDocumentsToAccount = async () => {
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  const imported = await importGuestDocuments();
  if (imported.length && isCurrentScope(scope, generation)) {
    const currentIndex = appJotaiStore.get(documentsAtom);
    const importedIds = new Set(imported.map((meta) => meta.id));
    const nextIndex = [
      ...currentIndex.filter((meta) => !importedIds.has(meta.id)),
      ...imported,
    ];
    appJotaiStore.set(documentsAtom, nextIndex);
    await documentStorage.saveIndex(nextIndex, scope);
  }
  return imported.length;
};

const createDocumentMeta = (name: string): DocumentMeta => {
  const now = Date.now();
  return { id: randomId(), name, createdAt: now, updatedAt: now };
};

/**
 * Loads the document index and returns the active document's data. On first
 * ever run, migrates the legacy single-scene localStorage data into an
 * "Untitled" document (or creates an empty one if there's nothing to
 * migrate).
 */
export const initializeDocuments = async (localDataState: {
  elements: ExcalidrawElement[];
  appState: Partial<AppState> | null;
}): Promise<{ id: string; data: StoredDocumentData }> => {
  if (!initialized) {
    initialized = true;

    let index = await documentStorage.loadIndex();

    if (!index.length) {
      const meta = createDocumentMeta(DEFAULT_DOCUMENT_NAME);
      const shouldMigrateLegacyScene = documentStorage.getScope() === "guest";
      const initialData = {
        elements: shouldMigrateLegacyScene ? localDataState.elements : [],
        appState:
          shouldMigrateLegacyScene && localDataState.appState
            ? clearAppStateForLocalStorage(localDataState.appState)
            : {},
      };
      await documentStorage.saveDocument(meta.id, {
        elements: initialData.elements,
        appState: initialData.appState,
      });
      index = [meta];
      await documentStorage.saveIndex(index);
      if (hasCloudUser()) {
        try {
          index = [await createCloudDocument(meta, initialData)];
          await documentStorage.saveIndex(index);
        } catch (error) {
          index = [{ ...meta, syncState: "offline" }];
          await documentStorage.saveIndex(index);
          console.error(error);
        }
      }
    }

    appJotaiStore.set(documentsAtom, index);

    // Links from approved integrations select only an existing document in
    // the current account's synchronized index. A URL never grants access.
    const linkedId = new URLSearchParams(window.location.search).get(
      "document",
    );
    const persistedId =
      linkedId && index.some((meta) => meta.id === linkedId)
        ? linkedId
        : readPersistedActiveDocumentId();
    const activeMeta =
      index.find((meta) => meta.id === persistedId) ||
      [...index].sort((a, b) => b.updatedAt - a.updatedAt)[0];
    setActiveDocumentId(activeMeta.id);
  }

  const activeDocumentId = appJotaiStore.get(activeDocumentIdAtom)!;
  const data = (await documentStorage.loadDocument(activeDocumentId)) || {
    elements: [],
    appState: {},
  };
  return { id: activeDocumentId, data };
};

// ---------------------------------------------------------------------------
// switching
// ---------------------------------------------------------------------------

export const switchToDocument = (
  id: string,
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  const operation = switchQueue
    .catch(() => undefined)
    .then(() => _switchToDocument(id, excalidrawAPI, scope, generation));
  switchQueue = operation.catch(() => undefined);
  return operation;
};

const _switchToDocument = async (
  id: string,
  excalidrawAPI: ExcalidrawImperativeAPI,
  scope: string,
  generation: number,
) => {
  if (!isCurrentScope(scope, generation)) {
    return;
  }
  if (id === appJotaiStore.get(activeDocumentIdAtom)) {
    return;
  }
  invalidatedCloudDocuments.delete(cloudQueueKey(scope, id));

  // persist pending changes of the document we're leaving
  flushDocumentSave();
  LocalData.flushSave();

  const data = (await documentStorage.loadDocument(id, scope)) || {
    elements: [],
    appState: {},
  };

  if (!isCurrentScope(scope, generation)) {
    return;
  }

  // repoint autosave at the new document before updateScene triggers onChange
  setActiveDocumentId(id);

  excalidrawAPI.updateScene({
    elements: restoreElements(data.elements, null, {
      repairBindings: true,
    }),
    appState: restoreAppState(data.appState, excalidrawAPI.getAppState()),
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });

  // undo history must not leak across documents
  excalidrawAPI.history.clear();

  const fileIds = data.elements.reduce((acc, element) => {
    if (isInitializedImageElement(element)) {
      return acc.concat(element.fileId);
    }
    return acc;
  }, [] as FileId[]);

  if (fileIds.length) {
    const { loadedFiles, erroredFiles } = await LocalData.fileStorage.getFiles(
      fileIds,
    );
    if (!isCurrentScope(scope, generation)) {
      return;
    }
    if (loadedFiles.length) {
      excalidrawAPI.addFiles(loadedFiles);
    }
    updateStaleImageStatuses({
      excalidrawAPI,
      erroredFiles,
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
    });
  }
};

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export const createDocument = async (
  parentPath: string | null,
  opts: { switchTo?: boolean; excalidrawAPI?: ExcalidrawImperativeAPI } = {},
): Promise<DocumentMeta> => {
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  const initialIndex = appJotaiStore.get(documentsAtom);
  const initialName = uniqueDocumentName(
    parentPath
      ? `${parentPath}/${DEFAULT_DOCUMENT_NAME}`
      : DEFAULT_DOCUMENT_NAME,
    initialIndex,
  );
  let meta = createDocumentMeta(initialName);

  await documentStorage.saveDocument(
    meta.id,
    { elements: [], appState: {} },
    scope,
  );
  if (!isCurrentScope(scope, generation)) {
    return meta;
  }
  const index = appJotaiStore.get(documentsAtom);
  meta = {
    ...meta,
    name: uniqueDocumentName(initialName, index),
  };
  const nextIndex = [...index, meta];
  appJotaiStore.set(documentsAtom, nextIndex);
  await documentStorage.saveIndex(nextIndex, scope);

  if (hasCloudUser(scope)) {
    try {
      const cloudMeta = await createCloudDocument(meta, {
        elements: [],
        appState: {},
      });
      await setDocumentSyncState(meta.id, cloudMeta, scope, generation);
    } catch (error) {
      await setDocumentSyncState(
        meta.id,
        { syncState: "offline" },
        scope,
        generation,
      );
      console.error(error);
    }
  }

  if (!isCurrentScope(scope, generation)) {
    return meta;
  }
  if (opts.switchTo && opts.excalidrawAPI) {
    await switchToDocument(meta.id, opts.excalidrawAPI);
  }

  return meta;
};

/**
 * Renames (or moves, if the path changes) a document. Returns the updated
 * meta, or null if the new name is unusable (caller should keep the old
 * name).
 */
export const renameDocument = async (
  id: string,
  nextName: string,
  excalidrawAPI?: ExcalidrawImperativeAPI,
): Promise<DocumentMeta | null> => {
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  const normalized = normalizeDocumentName(nextName);
  if (!normalized) {
    return null;
  }

  const index = appJotaiStore.get(documentsAtom);
  const meta = index.find((meta) => meta.id === id);
  if (!meta) {
    return null;
  }
  if (meta.name === normalized) {
    return meta;
  }

  const name = uniqueDocumentName(normalized, index, id);
  const nextMeta = { ...meta, name };
  const nextIndex = index.map((meta) => (meta.id === id ? nextMeta : meta));
  appJotaiStore.set(documentsAtom, nextIndex);
  await documentStorage.saveIndex(nextIndex, scope);
  if (!isCurrentScope(scope, generation)) {
    return null;
  }

  // keep the export filename of the open document in sync
  if (id === appJotaiStore.get(activeDocumentIdAtom) && excalidrawAPI) {
    excalidrawAPI.updateScene({
      appState: { name: getDocumentBasename(name) },
    });
  }

  if (hasCloudUser(scope)) {
    const data = await documentStorage.loadDocument(id, scope);
    if (data) {
      queueCloudSave(id, data, {}, scope, generation);
    }
  }

  return nextMeta;
};

export const duplicateDocument = async (
  id: string,
  excalidrawAPI: ExcalidrawImperativeAPI,
): Promise<DocumentMeta | null> => {
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  let index = appJotaiStore.get(documentsAtom);
  let meta = index.find((meta) => meta.id === id);
  if (!meta) {
    return null;
  }

  // flush first so duplicating the active document copies the latest state
  await flushDocumentSaves();
  if (!isCurrentScope(scope, generation)) {
    return null;
  }
  index = appJotaiStore.get(documentsAtom);
  meta = index.find((item) => item.id === id);
  if (!meta) {
    return null;
  }

  const data = (await documentStorage.loadDocument(id, scope)) || {
    elements: [],
    appState: {},
  };
  let copy = createDocumentMeta(
    uniqueDocumentName(`${meta.name} (copy)`, index),
  );

  await documentStorage.saveDocument(copy.id, data, scope);
  if (!isCurrentScope(scope, generation)) {
    return null;
  }
  index = appJotaiStore.get(documentsAtom);
  if (!index.some((item) => item.id === id)) {
    return null;
  }
  copy = {
    ...copy,
    name: uniqueDocumentName(`${meta.name} (copy)`, index),
  };
  const nextIndex = [...index, copy];
  appJotaiStore.set(documentsAtom, nextIndex);
  await documentStorage.saveIndex(nextIndex, scope);

  if (hasCloudUser(scope)) {
    try {
      const cloudMeta = await createCloudDocument(copy, data);
      await setDocumentSyncState(copy.id, cloudMeta, scope, generation);
    } catch (error) {
      await setDocumentSyncState(
        copy.id,
        { syncState: "offline" },
        scope,
        generation,
      );
      console.error(error);
    }
  }

  if (!isCurrentScope(scope, generation)) {
    return null;
  }
  await switchToDocument(copy.id, excalidrawAPI);
  return copy;
};

/** Deletes a document. If it's the active one, switches to the most recently
 * updated remaining document (always keeps at least one document around). */
export const deleteDocument = async (
  id: string,
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const scope = documentStorage.getScope();
  const generation = scopeGeneration;
  let index = appJotaiStore.get(documentsAtom);
  let documentMeta = index.find((meta) => meta.id === id);
  if (!documentMeta) {
    return false;
  }

  cancelDocumentSave(id);
  await flushDocumentSaves();
  if (!isCurrentScope(scope, generation)) {
    return false;
  }
  index = appJotaiStore.get(documentsAtom);
  documentMeta = index.find((meta) => meta.id === id);
  if (!documentMeta) {
    return false;
  }
  if (hasCloudUser(scope)) {
    try {
      await deleteCloudDocument(id, documentMeta.revision);
    } catch (error) {
      if (!(error instanceof CloudDocumentError && error.status === 404)) {
        await setDocumentSyncState(
          id,
          { syncState: "offline" },
          scope,
          generation,
        );
        console.error(error);
        return false;
      }
    }
  }

  if (!isCurrentScope(scope, generation)) {
    return false;
  }

  index = appJotaiStore.get(documentsAtom);
  const nextIndex = index.filter((meta) => meta.id !== id);
  appJotaiStore.set(documentsAtom, nextIndex);
  await documentStorage.saveIndex(nextIndex, scope);
  await documentStorage.deleteDocument(id, scope);
  // comments are stored per document — drop them alongside it
  commentStorage.deleteComments(id, scope).catch((error) => {
    console.error(error);
  });

  if (!isCurrentScope(scope, generation)) {
    return true;
  }

  if (id === appJotaiStore.get(activeDocumentIdAtom)) {
    let target = [...nextIndex].sort((a, b) => b.updatedAt - a.updatedAt)[0];
    if (!target) {
      target = await createDocument(null);
      if (!isCurrentScope(scope, generation)) {
        return true;
      }
    }
    // don't let a pending autosave write into the deleted document, and
    // reset the active id so switchToDocument doesn't bail out early
    appJotaiStore.set(activeDocumentIdAtom, null);
    await switchToDocument(target.id, excalidrawAPI);
  }
  return true;
};

/** File ids referenced by any stored document, used to keep the shared files
 * store from garbage-collecting images of non-active documents. */
export const getAllDocumentsFileIds = async (): Promise<FileId[]> => {
  const index = await documentStorage.loadIndex();
  const fileIds = new Set<FileId>();
  await Promise.all(
    index.map(async (meta) => {
      const data = await documentStorage.loadDocument(meta.id);
      data?.elements?.forEach((element) => {
        if (isInitializedImageElement(element)) {
          fileIds.add(element.fileId);
        }
      });
    }),
  );
  return [...fileIds];
};
